import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import React, { useContext } from 'react';
import {
  Card,
  CardContent,
  EqualHeight,
  Grid,
  GridColumn,
  Heading,
  Icon,
  ITagProps,
  List,
  ListItem,
  Separator,
  Slider,
  Spinner,
  Text,
  THeadingLevel,
  Tooltip,
  TooltipToggle,
} from 'telia-front-react';
import GaPushOnMount from '../content/GaPushOnMount';
import GaDataStore from '../stores/GaDataStore';
import { CampaignInfo, PackageIds } from '../stores/MobileLifeStore';
import { useStores } from '../stores/RootStore';
import { normalizeId } from '../util/TextUtil';
import { twebTranslateOrEmpty } from '../util/TranslateUtil';
import PrivateMobileCardButtons from './PrivateMobileCardButtons';
import PrivateMobileCardPrice from './PrivateMobileCardPrice';

export interface PrivateMobileCardProps {
  formName: string;
  packageName: string;
  singleUserUrl: string;
  roamingAmount: number;
  packageIds: PackageIds;
  callsPackagePrice: number;
  singleUserDistributedPrice: number;
  campaigns?: CampaignInfo[];
  multiUserDistributedPrice?: number;
  multiUserRoamingAmount?: number;
  packageCustomDistributedPrices?: number[];
  tagTop?: ITagProps[] | boolean;
  forSingleUserOnly?: boolean;
  modalExtraContent?: React.ReactNode;
  hideViewDetailsButton?: boolean;
  inModal?: boolean;
  headingLevel?: THeadingLevel;
  additionalOptions?: string;
  internetAmount?: string;
  calls?: string;
  sms?: string;
}

const PrivateMobileCard = observer((props: PrivateMobileCardProps) => {
  const { mobileLifeStore } = useStores();
  const { translate, translateOrEmpty } = useContext(TranslationsContext);
  const {
    packageName,
    multiUserRoamingAmount,
    singleUserUrl,
    roamingAmount,
    packageIds,
    tagTop,
    forSingleUserOnly,
    singleUserDistributedPrice,
    hideViewDetailsButton,
    headingLevel = 3,
    inModal,
  } = props;
  const packageId = mobileLifeStore.getPackageId(packageIds);
  const isOnlyCallsPackage = singleUserDistributedPrice === 0;

  const getRoamingAmount = (): number => {
    return !!multiUserRoamingAmount && mobileLifeStore.selectedDeviceCount > 1 ? multiUserRoamingAmount : roamingAmount;
  };

  const getInternetAmountFromPackageName = (): string => {
    const internetAmount = (props.internetAmount || packageName).split(' ').shift();
    return isNaN(+internetAmount) ? translate('mobile.package.unlimited') : `${internetAmount} ${translate('mobile.details.inclusions.internetUnit')}`;
  };

  const getTagTop = (): ITagProps[] | boolean => {
    if (mobileLifeStore.hasPackageDiscount(packageId)) {
      const internetPrices = mobileLifeStore.getPackagePrices(packageId);

      if (twebTranslateOrEmpty(translateOrEmpty, `mobile.package.discount.${internetPrices?.offerInfo?.offerName}`)) {
        return [
          {
            text: translate(`mobile.package.discount.${internetPrices.offerInfo.offerName}`),
            purpose: 'campaign-3',
          },
        ];
      }
    }
    return tagTop ?? false;
  };

  const renderUserSlider = (): JSX.Element => (
    <Slider
      id={`${inModal ? 'modal-' : ''}user-select-${normalizeId(packageName)}`}
      className="text__margin-none text-small"
      label={translate('mobile.package.usersAmount')}
      helpMessage={translate('mobile.package.cheaperWithFamily')}
      start={mobileLifeStore.selectedDeviceCount}
      onChange={(newValue) => {
        GaDataStore.pushWithoutCmsData({
          event: 'AutoEvent',
          eventCategory: 'Mobiilne elu',
          eventAction: 'Packages',
          eventLabel: `Slider - current-user-selected-slider-${newValue}`,
        });
        mobileLifeStore.setSelectedDeviceCount(newValue as number, packageIds);
      }}
      range={{ min: 1, max: 5 }}
      step={1}
      pips={[1, 2, 3, 4, 5].map((sliderPip) => {
        return { value: sliderPip, label: sliderPip.toString() };
      })}
      aria-labelledBy={translate('mobile.package.usersAmount')}
    />
  );

  const renderSingleUserPackageInfo = (): JSX.Element => (
    <Grid className="text-small" align={['middle-xs']} noWrap={true} horizontalGutter="small">
      <GridColumn width={['min']}>
        <Icon modifiers="#contact" size="xs" />
      </GridColumn>
      <GridColumn width={['max']}>
        <span>{translate('mobile.package.singleUser')}</span>
      </GridColumn>
    </Grid>
  );

  const renderPackageIcon = (): JSX.Element => {
    const packageIncludesNetflix = packageName?.toLowerCase().includes('netflix');
    return (
      <Grid noWrap={true} align={['middle-xs']} horizontalGutter="xsmall" verticalGutter="none" aria-hidden={true}>
        <GridColumn width={['min']}>
          <Icon modifiers="#mobile" size="lg" />
        </GridColumn>
        <GridColumn width={['min']}>
          <Icon modifiers="#plus" size="xxs" style={!packageIncludesNetflix ? { visibility: 'hidden' } : undefined} />
        </GridColumn>
        <GridColumn width={['min']}>
          <Icon
            modifiers="#logo-netflix"
            size="xxl"
            style={{
              color: '#E50914',
              visibility: packageIncludesNetflix ? 'visible' : 'hidden',
            }}
          />
        </GridColumn>
      </Grid>
    );
  };

  return (
    <Card className="text-left" background={`${hideViewDetailsButton ? 'neutral-bg-level-3' : 'brand-secondary-bg-subtlest'}`}>
      <EqualHeight group="card-top">
        <CardContent tagTop={getTagTop()} padding={{ bottom: 'small' }}>
          <Text>
            {renderPackageIcon()}
            <EqualHeight group="title-and-arguments">
              <Heading level={headingLevel} className="h3" id={`${inModal ? 'modal-' : ''}mobile-package-title-${normalizeId(packageName)}`}>
                {packageName}
              </Heading>
              <Separator spacing="xsmall" color="invisible" />
              <List type="icon" iconPosition="middle" iconClassName="icon--sm">
                {!isOnlyCallsPackage && (
                  <>
                    <ListItem icon="#service-internet">
                      <strong>{getInternetAmountFromPackageName()} </strong>
                      {!forSingleUserOnly && `${translate('mobile.package.shareable')} `}
                      {translate('mobile.package.5gInternet')}
                    </ListItem>
                    <ListItem icon="#eu-et">
                      {translate('mobile.package.euVolume', {
                        amount: getRoamingAmount(),
                      })}
                    </ListItem>
                  </>
                )}
                <ListItem icon="#phone-message">
                  <Tooltip referenceElement={<TooltipToggle>{translate('mobile.package.callsAndMessages')}</TooltipToggle>}>
                    {translate('mobile.package.callsAndMessages.tooltipContent')}
                  </Tooltip>
                </ListItem>
              </List>
            </EqualHeight>
          </Text>
        </CardContent>
      </EqualHeight>
      <EqualHeight group="user-slider" align="bottom">
        <CardContent
          background={hideViewDetailsButton ? 'brand-secondary-bg-subtlest' : 'brand-secondary-bg-subtle'}
          padding={{
            top: 'small',
            bottom: forSingleUserOnly ? 'small' : 'none',
          }}
        >
          {forSingleUserOnly ? renderSingleUserPackageInfo() : renderUserSlider()}
        </CardContent>
      </EqualHeight>
      <CardContent padding={{ top: 'small' }}>
        <Text>
          <EqualHeight group="price" align="bottom">
            {mobileLifeStore.isPricesFetched && mobileLifeStore.hasPackageDiscount(packageId) && (
              <GaPushOnMount pushWhenVisible={true} data={{ eventLabel: `Show - Discount - ${packageName}` }} />
            )}
            {mobileLifeStore.isPricesFetched ? <PrivateMobileCardPrice {...props} /> : <Spinner />}
          </EqualHeight>
          <PrivateMobileCardButtons
            packageName={packageName}
            packageIds={packageIds}
            forSingleUserOnly={forSingleUserOnly}
            singleUserUrl={singleUserUrl}
            hideViewDetailsButton={hideViewDetailsButton}
            inModal={inModal}
          />
        </Text>
      </CardContent>
    </Card>
  );
});

export default PrivateMobileCard;
